import { supabase } from '../api/supabase';
import { 
  ImportType, 
  ImportSnapshot, 
  DriverSnapshot, 
  CSVAnalysis, 
  VerificationResult,
  VerificationSummary,
  DriverVerification,
  Discrepancy,
  ImportHistory,
  IMPORT_VERIFICATION_CONFIGS,
  DuplicateRecord
} from '../types/importVerification';

// Web-compatible hash function
const generateHash = async (data: string): Promise<string> => {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      // Use Web Crypto API for browsers
      const encoder = new TextEncoder();
      const data_encoded = encoder.encode(data);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data_encoded);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } else {
      // Fallback for environments without crypto
      let hash = 0;
      if (data.length === 0) return hash.toString();
      for (let i = 0; i < data.length; i++) {
        const char = data.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash).toString(16);
    }
  } catch (error) {
    console.warn('Hash generation failed, using fallback:', error);
    return Date.now().toString(16);
  }
};

export class ImportVerificationService {
  
  /**
   * Creates a snapshot of current data before import
   */
  async capturePreImportSnapshot(importType: ImportType): Promise<ImportSnapshot> {
    console.log(`📸 Capturing pre-import snapshot for ${importType}`);
    
    const snapshot = await this.getCurrentSnapshot(importType);
    console.log(`📊 Pre-import snapshot: ${snapshot.recordCount} records, AED ${snapshot.totalAmount}`);
    
    return snapshot;
  }

  /**
   * Creates a snapshot of current data after import
   */
  async capturePostImportSnapshot(importType: ImportType): Promise<ImportSnapshot> {
    console.log(`📸 Capturing post-import snapshot for ${importType}`);
    
    const snapshot = await this.getCurrentSnapshot(importType);
    console.log(`📊 Post-import snapshot: ${snapshot.recordCount} records, AED ${snapshot.totalAmount}`);
    
    return snapshot;
  }

  /**
   * Gets current state of data for an import type
   */
  private async getCurrentSnapshot(importType: ImportType): Promise<ImportSnapshot> {
    let tableName: string;
    let amountField: string;
    
    // Map import type to table and amount field
    switch (importType) {
      case 'careem':
        tableName = 'careem_captain_data';
        amountField = 'net_fare';
        break;
      case 'uber':
        tableName = 'uber_captain_data';
        amountField = 'fare_amount';
        break;
      case 'salik':
        tableName = 'salik_data';
        amountField = 'amount';
        break;
      case 'fines':
        tableName = 'traffic_fines';
        amountField = 'amount';
        break;
      default:
        throw new Error(`Unknown import type: ${importType}`);
    }

    // Get overall totals
    const { data: totalData } = await supabase
      .from(tableName)
      .select(`${amountField}`)
      .not(amountField, 'is', null);

    const totalAmount = totalData?.reduce((sum, record) => sum + (record[amountField] || 0), 0) || 0;
    const recordCount = totalData?.length || 0;

    // Get driver breakdown
    const { data: driverData } = await supabase
      .from(tableName)
      .select(`
        driver_id,
        ${amountField},
        drivers!inner(name)
      `)
      .not(amountField, 'is', null);

    const driverBreakdown: DriverSnapshot[] = [];
    const driverMap = new Map<string, { amount: number; count: number; name: string }>();

    driverData?.forEach((record: any) => {
      const driverId = record.driver_id;
      const amount = record[amountField] || 0;
      const name = record.drivers?.name || 'Unknown Driver';
      
      if (driverMap.has(driverId)) {
        const existing = driverMap.get(driverId)!;
        existing.amount += amount;
        existing.count += 1;
      } else {
        driverMap.set(driverId, { amount, count: 1, name });
      }
    });

    driverMap.forEach((data, driverId) => {
      driverBreakdown.push({
        driverId,
        driverName: data.name,
        totalAmount: data.amount,
        recordCount: data.count
      });
    });

    // Create checksum for integrity verification
    const checksumData = {
      totalAmount,
      recordCount,
      driverCount: driverBreakdown.length,
      lastSnapshot: new Date().toISOString()
    };
    const checksum = await generateHash(JSON.stringify(checksumData));

    return {
      totalAmount,
      recordCount,
      checksum,
      capturedAt: new Date().toISOString(),
      driverBreakdown
    };
  }

  /**
   * Analyzes CSV data before import to detect duplicates and calculate expected changes
   */
  async analyzeCsvData(importType: ImportType, csvData: any[]): Promise<CSVAnalysis> {
    console.log(`🔍 Analyzing CSV data for ${importType}: ${csvData.length} records`);
    
    const config = IMPORT_VERIFICATION_CONFIGS[importType];
    const duplicatesFound: DuplicateRecord[] = [];
    const validRecords: any[] = [];
    const rejectedRecords: any[] = [];

    // Check each CSV record for duplicates
    for (let i = 0; i < csvData.length; i++) {
      const csvRecord = csvData[i];
      const duplicateInfo = await this.checkForDuplicate(importType, csvRecord, config.duplicateCriteria);
      
      if (duplicateInfo) {
        duplicatesFound.push({
          csvRowNumber: i + 2, // +2 for 1-based indexing and header row
          csvData: csvRecord,
          duplicateCriteria: config.duplicateCriteria.join('+'),
          existingRecordId: duplicateInfo.id,
          existingRecordData: duplicateInfo,
          rejectionReason: `Duplicate found matching: ${config.duplicateCriteria.join(', ')}`
        });
        rejectedRecords.push(csvRecord);
      } else {
        validRecords.push(csvRecord);
      }
    }

    // Calculate totals from valid records
    const amountField = this.getAmountFieldForImportType(importType);
    const totalAmount = validRecords.reduce((sum, record) => sum + (parseFloat(record[amountField]) || 0), 0);
    const recordCount = validRecords.length;

    // Create driver breakdown from CSV
    const driverBreakdown = this.createDriverBreakdownFromCsv(validRecords, importType);

    // Create checksum
    const checksumData = { totalAmount, recordCount, duplicatesFound: duplicatesFound.length };
    const checksum = await generateHash(JSON.stringify(checksumData));

    console.log(`📊 CSV Analysis: ${validRecords.length} valid, ${duplicatesFound.length} duplicates, AED ${totalAmount}`);

    return {
      totalAmount,
      recordCount,
      checksum,
      driverBreakdown,
      duplicatesFound,
      validRecords,
      rejectedRecords
    };
  }

  /**
   * Performs verification by comparing snapshots
   */
  async performVerification(
    preSnapshot: ImportSnapshot,
    csvAnalysis: CSVAnalysis,
    postSnapshot: ImportSnapshot,
    importType: ImportType
  ): Promise<VerificationResult> {
    console.log(`🔍 Performing verification for ${importType} import`);

    const config = IMPORT_VERIFICATION_CONFIGS[importType];
    
    // Calculate expected vs actual changes
    const expectedAmountIncrease = csvAnalysis.totalAmount;
    const actualAmountIncrease = postSnapshot.totalAmount - preSnapshot.totalAmount;
    const amountDiscrepancy = Math.abs(expectedAmountIncrease - actualAmountIncrease);

    const expectedCountIncrease = csvAnalysis.recordCount;
    const actualCountIncrease = postSnapshot.recordCount - preSnapshot.recordCount;
    const countDiscrepancy = Math.abs(expectedCountIncrease - actualCountIncrease);

    // Check if verification passed
    const amountVerificationPassed = amountDiscrepancy <= config.toleranceAmount;
    const countVerificationPassed = countDiscrepancy === 0; // Count must be exact
    const overallVerificationPassed = amountVerificationPassed && countVerificationPassed;

    // Create summary
    const summary: VerificationSummary = {
      expectedAmountIncrease,
      actualAmountIncrease,
      amountDiscrepancy,
      expectedCountIncrease,
      actualCountIncrease,
      countDiscrepancy,
      csvRecordsProcessed: csvAnalysis.recordCount + csvAnalysis.duplicatesFound.length,
      recordsImported: csvAnalysis.recordCount,
      recordsRejected: csvAnalysis.rejectedRecords.length,
      duplicatesFound: csvAnalysis.duplicatesFound.length,
      amountVerificationPassed,
      countVerificationPassed,
      overallVerificationPassed
    };

    // Create driver-level verification
    const driverDetails = this.createDriverVerification(preSnapshot, csvAnalysis, postSnapshot);

    // Create discrepancy list
    const discrepancies = this.createDiscrepancyList(summary, driverDetails, config);

    console.log(`✅ Verification ${overallVerificationPassed ? 'PASSED' : 'FAILED'}`);

    return {
      passed: overallVerificationPassed,
      summary,
      driverDetails,
      duplicateLog: csvAnalysis.duplicatesFound,
      discrepancies
    };
  }

  /**
   * Saves import history and verification results to database
   */
  async saveImportHistory(
    importType: ImportType,
    fileName: string,
    preSnapshot: ImportSnapshot,
    csvAnalysis: CSVAnalysis,
    postSnapshot: ImportSnapshot,
    verificationResult: VerificationResult,
    processingTimeMs: number
  ): Promise<string> {
    console.log(`💾 Saving import history for ${importType}`);

    const { data, error } = await supabase
      .from('import_history')
      .insert({
        import_type: importType,
        file_name: fileName,
        status: verificationResult.passed ? 'completed' : 'verification_failed',
        
        pre_total_amount: preSnapshot.totalAmount,
        pre_record_count: preSnapshot.recordCount,
        pre_checksum: preSnapshot.checksum,
        
        csv_total_amount: csvAnalysis.totalAmount,
        csv_record_count: csvAnalysis.recordCount,
        csv_checksum: csvAnalysis.checksum,
        
        post_total_amount: postSnapshot.totalAmount,
        post_record_count: postSnapshot.recordCount,
        post_checksum: postSnapshot.checksum,
        
        expected_increase_amount: verificationResult.summary.expectedAmountIncrease,
        actual_increase_amount: verificationResult.summary.actualAmountIncrease,
        expected_increase_count: verificationResult.summary.expectedCountIncrease,
        actual_increase_count: verificationResult.summary.actualCountIncrease,
        
        verification_passed: verificationResult.passed,
        verification_notes: JSON.stringify(verificationResult.discrepancies),
        
        processing_time_ms: processingTimeMs,
        duplicates_found: verificationResult.summary.duplicatesFound,
        records_imported: verificationResult.summary.recordsImported,
        records_rejected: verificationResult.summary.recordsRejected
      })
      .select('id')
      .single();

    if (error) {
      throw new Error(`Failed to save import history: ${error.message}`);
    }

    // Save driver-level details
    await this.saveDriverVerificationDetails(data.id, verificationResult.driverDetails);

    // Save duplicate log
    await this.saveDuplicateLog(data.id, importType, verificationResult.duplicateLog);

    console.log(`✅ Import history saved with ID: ${data.id}`);
    return data.id;
  }

  // Helper methods...

  private async checkForDuplicate(importType: ImportType, csvRecord: any, criteria: string[]): Promise<any> {
    // Implementation to check for duplicates based on criteria
    // This would query the appropriate table to see if a record already exists
    // Return null if no duplicate, or the existing record if found
    return null; // Placeholder
  }

  private getAmountFieldForImportType(importType: ImportType): string {
    const fieldMap = {
      careem: 'net_fare',
      uber: 'fare_amount',
      salik: 'amount',
      fines: 'amount'
    };
    return fieldMap[importType];
  }

  private createDriverBreakdownFromCsv(validRecords: any[], importType: ImportType): DriverSnapshot[] {
    // Group CSV records by driver and calculate totals
    // Implementation here
    return []; // Placeholder
  }

  private createDriverVerification(
    preSnapshot: ImportSnapshot,
    csvAnalysis: CSVAnalysis,
    postSnapshot: ImportSnapshot
  ): DriverVerification[] {
    // Compare driver-level changes
    // Implementation here
    return []; // Placeholder
  }

  private createDiscrepancyList(
    summary: VerificationSummary,
    driverDetails: DriverVerification[],
    config: any
  ): Discrepancy[] {
    const discrepancies: Discrepancy[] = [];

    if (!summary.amountVerificationPassed) {
      discrepancies.push({
        type: 'amount',
        severity: 'critical',
        description: `Amount discrepancy: Expected increase of AED ${summary.expectedAmountIncrease}, but actual increase was AED ${summary.actualAmountIncrease}`,
        expected: summary.expectedAmountIncrease,
        actual: summary.actualAmountIncrease,
        difference: summary.amountDiscrepancy,
        suggestions: [
          'Check for data corruption during import',
          'Verify CSV data integrity',
          'Review duplicate detection logic'
        ]
      });
    }

    if (!summary.countVerificationPassed) {
      discrepancies.push({
        type: 'count',
        severity: 'critical',
        description: `Record count discrepancy: Expected ${summary.expectedCountIncrease} new records, but ${summary.actualCountIncrease} were added`,
        expected: summary.expectedCountIncrease,
        actual: summary.actualCountIncrease,
        difference: summary.countDiscrepancy,
        suggestions: [
          'Check for failed inserts',
          'Review database constraints',
          'Verify import transaction completed'
        ]
      });
    }

    return discrepancies;
  }

  private async saveDriverVerificationDetails(importHistoryId: string, driverDetails: DriverVerification[]): Promise<void> {
    // Save driver-level verification details
    // Implementation here
  }

  private async saveDuplicateLog(importHistoryId: string, importType: ImportType, duplicateLog: DuplicateRecord[]): Promise<void> {
    // Save duplicate detection log
    // Implementation here
  }

  /**
   * Mark an import as accepted after verification review
   */
  async markImportAsAccepted(importHistoryId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('import_history')
        .update({
          verification_status: 'accepted',
          reviewed_at: new Date().toISOString(),
          verification_notes: 'Import verified and accepted by user'
        })
        .eq('id', importHistoryId);

      if (error) {
        throw new Error(`Failed to mark import as accepted: ${error.message}`);
      }

      console.log(`✅ Import ${importHistoryId} marked as accepted`);
    } catch (error) {
      console.error('Error marking import as accepted:', error);
      throw error;
    }
  }

  /**
   * Mark an import as rejected after verification review
   */
  async markImportAsRejected(importHistoryId: string, reason?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('import_history')
        .update({
          verification_status: 'rejected',
          reviewed_at: new Date().toISOString(),
          verification_notes: reason || 'Import rejected due to verification issues'
        })
        .eq('id', importHistoryId);

      if (error) {
        throw new Error(`Failed to mark import as rejected: ${error.message}`);
      }

      console.log(`❌ Import ${importHistoryId} marked as rejected`);
    } catch (error) {
      console.error('Error marking import as rejected:', error);
      throw error;
    }
  }
}

export const importVerificationService = new ImportVerificationService();
